/**
 * What a man does with his hands (05 §6.2): recoil, the hit stagger and raised hands as
 * OVERLAYS on whatever the legs are doing, and the two keyed one-shots - the throw and the
 * knife thrust. Nothing here glides: overlays come in two or three strengths, one-shots are
 * read on the 12 Hz pose grid.
 */

import { type BoneOf, carry, PELVIS_HEIGHT, type Rig, steppedPhase } from './poseBase';
import { type Keys, playKeys } from './poseBlend';
import { reachLeg } from './poseLegs';
import { B } from './rig';

/** Wind-up, release, follow-through, back to the carry. The sim's `throw` event fires at the RELEASE. */
export const THROW_SECONDS = 0.6;
/** Fraction of the throw at which the grenade leaves the hand: start here when the event arrives. */
export const THROW_RELEASE_AT = 0.34;
/** The knife's cooldown (sim KNIFE.cooldown) is the length of the thrust. */
export const STAB_SECONDS = 0.3;
/** Matches the sim's stagger (RANGER.staggerTicks): he is slowed for exactly as long as he reels. */
export const HIT_SECONDS = 0.35;

/** Recoil strength for a shot fired `age` seconds ago: a hard kick, a half-recovered frame, then steady. */
export function kickAt(age: number): number {
  return age < 0.05 ? 1 : age < 0.1 ? 0.4 : 0;
}

/** Stagger strength `age` seconds after a hit, in three held steps. */
export function flinchAt(age: number): number {
  return age < 0.1 ? 1 : age < 0.22 ? 0.6 : age < HIT_SECONDS ? 0.25 : 0;
}

/** Shoulder rocks back, muzzle climbs, the rifle comes back into him. For a standing man. */
export function kickStanding(bone: BoneOf, k: number): void {
  if (k <= 0) return;
  bone(B.spine).rotation.x -= 0.1 * k;
  bone(B.head).rotation.x -= 0.05 * k;
  bone(B.lUpperArm).rotation.x += 0.12 * k;
  bone(B.rUpperArm).rotation.x += 0.14 * k;
  bone(B.weapon).position.z -= 0.07 * k;
  bone(B.weapon).rotation.x -= 0.1 * k;
}

/** Prone, the rifle runs back along the ground (the body's local +y) and the head comes down behind it. */
export function kickProne(bone: BoneOf, k: number): void {
  if (k <= 0) return;
  bone(B.weapon).position.y -= 0.08 * k;
  bone(B.head).rotation.x += 0.1 * k;
  bone(B.lUpperArm).rotation.z -= 0.06 * k;
  bone(B.rUpperArm).rotation.z += 0.06 * k;
}

/** Struck: chest thrown back and round, head down, the off hand flung out, muzzle dropped. */
export function flinch(bone: BoneOf, k: number): void {
  if (k <= 0) return;
  bone(B.spine).rotation.x -= 0.35 * k;
  bone(B.spine).rotation.z += 0.18 * k;
  bone(B.head).rotation.x += 0.3 * k;
  bone(B.pelvis).position.y -= 0.06 * k;
  bone(B.lUpperArm).rotation.z += 0.55 * k;
  bone(B.lForeArm).rotation.x += 0.5 * k;
  bone(B.weapon).rotation.x += 0.35 * k;
  bone(B.weapon).position.y -= 0.08 * k;
}

/** Upper arms out and up, forearms vertical: unmistakable at 28 px. The weapon bone is left alone. */
export function handsUp(bone: BoneOf): void {
  bone(B.lUpperArm).rotation.set(-0.15, 0, 2.2);
  bone(B.lForeArm).rotation.set(0, 0, 0.94);
  bone(B.rUpperArm).rotation.set(-0.15, 0, -2.2);
  bone(B.rForeArm).rotation.set(0, 0, -0.94);
  bone(B.head).rotation.x += 0.12;
}

/* ----------------------------------------------------------------------------- throw */

/** Feet apart, left foot leading: what both the throw and the thrust stand on. */
function braced(bone: BoneOf, lead: number): void {
  bone(B.lThigh).rotation.x = -0.3 * lead;
  bone(B.lShin).rotation.x = 0.35 * lead;
  bone(B.rThigh).rotation.x = 0.25 * lead;
  bone(B.rShin).rotation.x = 0.2;
  bone(B.pelvis).position.y = PELVIS_HEIGHT - 0.05 * lead;
}

/** The rifle stays in the left hand across the chest; the right arm is free. */
const windUp = (bone: BoneOf): void => {
  carry(bone);
  braced(bone, 1);
  bone(B.spine).rotation.set(-0.12, -0.5, 0);
  bone(B.head).rotation.y = 0.35;
  // Arm up and back, hand by the ear.
  bone(B.rUpperArm).rotation.set(2.4, 0, -0.25);
  bone(B.rForeArm).rotation.set(-1.3, 0, 0);
};

const release = (bone: BoneOf): void => {
  carry(bone);
  braced(bone, 1.3);
  bone(B.spine).rotation.set(0.3, 0.45, 0);
  bone(B.rUpperArm).rotation.set(-2.2, 0, -0.1);
  bone(B.rForeArm).rotation.set(-0.2, 0, 0);
};

const followThrough = (bone: BoneOf): void => {
  carry(bone);
  braced(bone, 1.5);
  bone(B.spine).rotation.set(0.42, 0.6, 0);
  bone(B.head).rotation.x = -0.25;
  bone(B.rUpperArm).rotation.set(-0.9, 0, 0.25);
  bone(B.rForeArm).rotation.set(-0.1, 0, 0);
};

const THROW_KEYS: Keys = [[0, windUp], [THROW_RELEASE_AT, release], [0.62, followThrough], [1, carry]];

/** `age` 0 is the cocked arm, which is also how a man with a grenade in his hand stands and waits. */
export function throwPose(rig: Rig, age: number): void {
  playKeys(rig, THROW_KEYS, steppedPhase(age, THROW_SECONDS));
}

/* ------------------------------------------------------------------------------ stab */

/**
 * The knife reaches 1.8 m (ROUND7: a short lunge), which an arm alone cannot: the whole man goes at him.
 * The sim does not move him, so the lunge is carried in the pose - the hips travel this far forward over a
 * long front step and come back as he recovers. The back foot stays where he stood.
 */
export const LUNGE_REACH = 0.42;
/** Pelvis height at full stretch: down over the bent front knee. */
const LUNGE_PELVIS = 0.64;

/** Coiled: weight back and down, the left shoulder leading, knife hand drawn back to the hip. */
const drawBack = (bone: BoneOf): void => {
  carry(bone);
  const pelvisY = PELVIS_HEIGHT - 0.14;
  bone(B.pelvis).position.set(0, pelvisY, -0.06);
  reachLeg(bone, B.lThigh, B.lShin, pelvisY, 0.3);
  reachLeg(bone, B.rThigh, B.rShin, pelvisY, -0.14);
  bone(B.spine).rotation.set(0.18, -0.4, 0);
  bone(B.head).rotation.x = -0.12;
  bone(B.rUpperArm).rotation.set(0.7, 0, -0.15);
  bone(B.rForeArm).rotation.set(-1.6, 0, 0);
};

/** Full stretch: hips low and forward over the front knee, chest over them, the knife arm driven out straight. */
const lunge = (bone: BoneOf): void => {
  carry(bone);
  bone(B.pelvis).position.set(0, LUNGE_PELVIS, LUNGE_REACH);
  reachLeg(bone, B.lThigh, B.lShin, LUNGE_PELVIS, 0.5);
  reachLeg(bone, B.rThigh, B.rShin, LUNGE_PELVIS, -LUNGE_REACH - 0.02);
  // Chest pitched in and turned to bring the right shoulder forward, the arm level with his kidneys.
  bone(B.spine).rotation.set(0.4, 0.5, 0);
  bone(B.head).rotation.x = -0.3;
  bone(B.rUpperArm).rotation.set(-1.75, 0, 0.12);
  bone(B.rForeArm).rotation.set(-0.05, 0, 0);
};

/**
 * Read on four steps (three hundred milliseconds is under four pose frames: on the 12 Hz grid the thrust
 * would be missed): the coil, the lunge held for two, and a drawing half way back to the carry - so the
 * hips do not jump the whole reach back in one frame when whatever he does next takes over.
 */
const STAB_KEYS: Keys = [[0, drawBack], [0.25, lunge], [0.5, lunge], [1, carry]];

export function stabPose(rig: Rig, age: number): void {
  playKeys(rig, STAB_KEYS, Math.floor((age / STAB_SECONDS) * 4) / 4);
}
